import { useTheme } from '@automata/mui-theme';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import DOMPurify from 'dompurify';
import { ReactNode } from 'react';

import { Typography } from '../Typography';

type SubComponent<T extends string | ReactNode = string> = (props: {
  children: T;
}) => JSX.Element;

export const InfoPopoverFrame: SubComponent<ReactNode> = ({ children }) => (
  <Box p={2}>{children}</Box>
);

export const InfoPopoverTitle: SubComponent = ({ children }) => (
  <Typography variant="subtitle1">{children}</Typography>
);

export const InfoPopoverCopy: SubComponent<ReactNode> = ({ children }) => {
  // If children is a string, use dangerouslySetInnerHTML for HTML support
  if (typeof children === 'string') {
    return (
      <Typography
        variant="body2"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(children) }}
      />
    );
  }

  // If children is JSX, render it directly
  return (
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  );
};

export const InfoPopoverExampleBlock: SubComponent = ({ children }) => {
  const theme = useTheme();
  return (
    <Box p={1} borderRadius={1} bgcolor={theme.palette.grey[100]}>
      <Typography variant="overline">Example</Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
};

export type InfoPopoverProps = {
  title?: string;
  copy?: string | ReactNode;
  example?: string;
};

export const InfoPopover = ({
  title,
  copy,
  example,
}: InfoPopoverProps): JSX.Element | null => {
  if (!title && !copy && !example) return null;

  return (
    <InfoPopoverFrame>
      {title && <InfoPopoverTitle>{title}</InfoPopoverTitle>}
      {(copy || example) && (
        <Stack gap={2}>
          {copy && <InfoPopoverCopy>{copy}</InfoPopoverCopy>}
          {example && (
            <InfoPopoverExampleBlock>{example}</InfoPopoverExampleBlock>
          )}
        </Stack>
      )}
    </InfoPopoverFrame>
  );
};
